/**
 * Global Error Boundary Component
 * Catches JavaScript errors anywhere in the component tree and displays a fallback UI
 * 
 * Features:
 * - Catches React rendering errors, lifecycle method errors, and constructor errors
 * - Provides detailed error logging with stack traces
 * - Shows user-friendly error modal with recovery options
 * - Integrates with global error context for consistent error handling
 */

import React from 'react';
import PropTypes from 'prop-types';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box,
  Alert,
  AlertTitle,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { 
  Error as ErrorIcon, 
  Refresh as RefreshIcon, 
  Home as HomeIcon,
  ExpandMore as ExpandMoreIcon 
} from '@mui/icons-material';
import { withStyles } from '@material-ui/core/styles';

const styles = (theme) => ({
  errorDialog: {
    '& .MuiDialog-paper': {
      minWidth: '400px',
      maxWidth: '600px',
      [theme.breakpoints.down('sm')]: {
        minWidth: '90vw',
        margin: '16px',
      },
    },
  },
  errorIcon: {
    color: '#ca453e',
    fontSize: '48px',
    marginBottom: '16px',
  },
  errorTitle: {
    color: '#26516F',
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: '16px',
  },
  errorMessage: {
    marginBottom: '16px',
    textAlign: 'center',
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    flexWrap: 'wrap',
    '& .MuiButton-root': {
      minWidth: '120px',
    },
  },
  refreshButton: {
    backgroundColor: '#26516F',
    color: 'white',
    '&:hover': {
      backgroundColor: '#1e3f5a',
    },
  },
  homeButton: {
    backgroundColor: '#ca453e',
    color: 'white',
    '&:hover': {
      backgroundColor: '#a83630',
    },
  },
  technicalDetails: {
    marginTop: '16px',
    '& .MuiAccordionSummary-root': {
      backgroundColor: '#f5f5f5',
    },
  },
  errorStack: {
    backgroundColor: '#f8f9fa',
    padding: '12px',
    borderRadius: '4px',
    fontFamily: 'monospace',
    fontSize: '12px',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '200px',
    overflow: 'auto',
  },
});

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showTechnicalDetails: false,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error details
    this.setState({
      error,
      errorInfo,
    });

    // Log to console for debugging
    console.error('Error Boundary caught an error:', error);
    console.error('Error Info:', errorInfo);

    // Send error to logging service if available
    this.logErrorToService(error, errorInfo);

    // Report to global error context if available
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  logErrorToService = (error, errorInfo) => {
    try {
      // Log error with timestamp and user agent
      const errorLog = {
        timestamp: new Date().toISOString(),
        error: {
          message: error.message,
          stack: error.stack,
          name: error.name,
        },
        errorInfo: {
          componentStack: errorInfo.componentStack,
        },
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: this.props.userId || 'anonymous',
      };

      // In a production environment, you would send this to your logging service
      console.error('Error Log:', JSON.stringify(errorLog, null, 2));
      
      // Store in localStorage for potential recovery
      localStorage.setItem('lastError', JSON.stringify(errorLog));
    } catch (loggingError) {
      console.error('Failed to log error:', loggingError);
    }
  };

  handleRefresh = () => {
    // Clear error state and reload the page
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
    window.location.reload();
  };

  handleGoHome = () => {
    // Clear error state and navigate to home
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
    window.location.href = '/kabahagi';
  };

  handleRetry = () => {
    // Clear error state and try to recover
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  getErrorMessage = () => {
    const { error } = this.state;
    const { customErrorMessage } = this.props;

    if (customErrorMessage) {
      return customErrorMessage;
    }

    if (error) {
      // Provide user-friendly messages for common errors
      if (error.message.includes('ChunkLoadError') || error.message.includes('Loading chunk')) {
        return 'We\'ve updated the application with new features. Please refresh your page to get the latest version.';
      }
      if (error.message.includes('Network Error') || error.message.includes('fetch')) {
        return 'We\'re having trouble connecting to our services. Please check your internet connection and try again.';
      }
      if (error.message.includes('Script error')) {
        return 'There seems to be a compatibility issue. Please try refreshing the page or using a different browser.';
      }
      if (error.name === 'SyntaxError') {
        return 'We encountered a technical hiccup. Please refresh the page to continue.';
      }
    }

    return 'Oops! Something unexpected happened, but don\'t worry - your information is safe. Please try refreshing the page, and if this keeps happening, let us know so we can help.';
  };

  getErrorTitle = () => {
    const { error } = this.state;
    
    if (error) {
      if (error.message.includes('ChunkLoadError') || error.message.includes('Loading chunk')) {
        return 'App Update Available';
      }
      if (error.message.includes('Network Error') || error.message.includes('fetch')) {
        return 'Connection Issue';
      }
    }
    
    return 'Something Went Wrong';
  };

  render() {
    const { classes, children, fallback } = this.props;
    const { hasError, error, errorInfo } = this.state;

    if (hasError) {
      // Render custom fallback UI if provided
      if (fallback) {
        return fallback;
      }

      // Render default error modal
      return (
        <Dialog
          open={true}
          className={classes.errorDialog}
          disableEscapeKeyDown
          disableBackdropClick
        >
          <DialogTitle>
            <Box display="flex" flexDirection="column" alignItems="center">
              <ErrorIcon className={classes.errorIcon} />
              <Typography variant="h5" className={classes.errorTitle}>
                {this.getErrorTitle()}
              </Typography>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>We're Sorry for the Inconvenience</AlertTitle>
              <Typography className={classes.errorMessage}>
                {this.getErrorMessage()}
              </Typography>
            </Alert>

            <Accordion className={classes.technicalDetails}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">
                  Technical Information (for support team)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Error Message:
                  </Typography>
                  <Box className={classes.errorStack}>
                    {error ? error.toString() : 'Unknown error'}
                  </Box>
                  
                  {error && error.stack && (
                    <>
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Stack Trace:
                      </Typography>
                      <Box className={classes.errorStack}>
                        {error.stack}
                      </Box>
                    </>
                  )}

                  {errorInfo && errorInfo.componentStack && (
                    <>
                      <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                        Component Stack:
                      </Typography>
                      <Box className={classes.errorStack}>
                        {errorInfo.componentStack}
                      </Box>
                    </>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          </DialogContent>

          <DialogActions className={classes.actionButtons}>
            <Button
              onClick={this.handleRetry}
              variant="outlined"
              startIcon={<RefreshIcon />}
            >
              Try Again
            </Button>
            <Button
              onClick={this.handleRefresh}
              variant="contained"
              className={classes.refreshButton}
              startIcon={<RefreshIcon />}
            >
              Refresh Page
            </Button>
            <Button
              onClick={this.handleGoHome}
              variant="contained"
              className={classes.homeButton}
              startIcon={<HomeIcon />}
            >
              Take Me Home
            </Button>
          </DialogActions>
        </Dialog>
      );
    }

    return children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  fallback: PropTypes.node,
  onError: PropTypes.func,
  customErrorMessage: PropTypes.string,
  userId: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

ErrorBoundary.defaultProps = {
  fallback: null,
  onError: null,
  customErrorMessage: null,
  userId: null,
};

export default withStyles(styles)(ErrorBoundary);